import { mod } from '../util/math';

export type PitchClass = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

/** Chord vocabulary: the MVP triads plus the V1 qualities (F2.4). */
export type ChordQuality =
  'maj' | 'min' | '7' | 'maj7' | 'm7' | '6' | 'add9' | 'sus2' | 'sus4' | 'dim' | 'aug';

/** A chord as it sounds: no spelling, no transpose or capo (those are display concerns). */
export interface Chord {
  root: PitchClass;
  quality: ChordQuality;
}

/** How much detail chord names keep (F2.11). */
export type Complexity = 'simple' | 'standard' | 'advanced';

interface QualityInfo {
  /** Semitones above the root, root first. */
  intervals: number[];
  /** Label suffix after the root: "", "m", "7", "maj7", … */
  suffix: string;
  /** The major or minor triad a player can fall back to (simple mode, majmin evaluation). */
  triad: 'maj' | 'min';
  /** What the quality becomes in standard mode. */
  standard: ChordQuality;
}

export const QUALITIES: Record<ChordQuality, QualityInfo> = {
  maj: { intervals: [0, 4, 7], suffix: '', triad: 'maj', standard: 'maj' },
  min: { intervals: [0, 3, 7], suffix: 'm', triad: 'min', standard: 'min' },
  '7': { intervals: [0, 4, 7, 10], suffix: '7', triad: 'maj', standard: '7' },
  maj7: { intervals: [0, 4, 7, 11], suffix: 'maj7', triad: 'maj', standard: 'maj7' },
  m7: { intervals: [0, 3, 7, 10], suffix: 'm7', triad: 'min', standard: 'm7' },
  '6': { intervals: [0, 4, 7, 9], suffix: '6', triad: 'maj', standard: 'maj' },
  add9: { intervals: [0, 2, 4, 7], suffix: 'add9', triad: 'maj', standard: 'maj' },
  sus2: { intervals: [0, 2, 7], suffix: 'sus2', triad: 'maj', standard: 'sus2' },
  sus4: { intervals: [0, 5, 7], suffix: 'sus4', triad: 'maj', standard: 'sus4' },
  dim: { intervals: [0, 3, 6], suffix: 'dim', triad: 'min', standard: 'dim' },
  aug: { intervals: [0, 4, 8], suffix: 'aug', triad: 'maj', standard: 'maj' },
};

export const CHORD_QUALITIES = Object.keys(QUALITIES) as ChordQuality[];

export const SHARP_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

export function toPitchClass(n: number): PitchClass {
  return mod(Math.round(n), 12) as PitchClass;
}

/** Pitch classes of the chord, root first. */
export function chordTones(chord: Chord): PitchClass[] {
  return QUALITIES[chord.quality].intervals.map((iv) => toPitchClass(chord.root + iv));
}

/**
 * Major or minor for a chord without a third (sus2, sus4): the triad on that root that belongs
 * to the key, so Esus4 in G major reads as Em (the vi chord), Dsus4 as D. Major if neither.
 */
export function triadInKey(
  root: number,
  key: { tonic: number; mode: 'major' | 'minor' },
): 'maj' | 'min' {
  const degree = mod(root - key.tonic, 12);
  const minorDegrees = key.mode === 'major' ? [2, 4, 9] : [0, 5, 7];
  return minorDegrees.includes(degree) ? 'min' : 'maj';
}

/**
 * The chord at a complexity level (F2.11): simple keeps major/minor only. With the song's key,
 * sus chords simplify to the triad that fits the key instead of always major.
 */
export function simplify(
  chord: Chord,
  complexity: Complexity,
  key?: { tonic: number; mode: 'major' | 'minor' },
): Chord {
  if (complexity === 'advanced') return chord;
  const info = QUALITIES[chord.quality];
  let quality = complexity === 'simple' ? info.triad : info.standard;
  const sus = chord.quality === 'sus2' || chord.quality === 'sus4';
  if (sus && complexity === 'simple' && key) quality = triadInKey(chord.root, key);
  return quality === chord.quality ? chord : { root: chord.root, quality };
}

/** Plain ASCII label used in engine output, tests and annotation files: "C", "C#m7", "N". */
export function chordLabel(chord: Chord | null): string {
  if (!chord) return 'N';
  return SHARP_NAMES[chord.root] + QUALITIES[chord.quality].suffix;
}

export function chordsEqual(a: Chord | null, b: Chord | null): boolean {
  if (!a || !b) return a === b;
  return a.root === b.root && a.quality === b.quality;
}
